import {
  Bell,
  BookOpen,
  Building2,
  CopyCheck,
  Database,
  History,
  House,
  Image,
  Landmark,
  Inbox,
  LucideIcon,
  Network,
  Radar,
  Scale,
  Search,
  ShieldCheck,
  Sparkles,
  Tags,
  UserCog,
  Users,
  Waypoints,
} from 'lucide-react';
import { User } from '../../types/auth';

type Role = User['role'];

export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  roles: Role[];
  /** Label under the icon in the mobile tab bar */
  tabLabel?: string;
}

const EVERYONE: Role[] = ['super_admin', 'admin', 'top_owner', 'user'];
const ADMINS: Role[] = ['super_admin', 'admin'];

export const PRIMARY_NAV: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: House, roles: EVERYONE, tabLabel: 'Home' },
  { name: 'Stakeholders', href: '/stakeholders', icon: Users, roles: EVERYONE, tabLabel: 'People' },
  { name: 'Organizations', href: '/organizations', icon: Building2, roles: EVERYONE, tabLabel: 'Orgs' },
  { name: 'Government', href: '/government', icon: Landmark, roles: EVERYONE },
  { name: 'Wiki', href: '/wiki', icon: BookOpen, roles: EVERYONE, tabLabel: 'Wiki' },
  { name: 'Media', href: '/media', icon: Image, roles: EVERYONE },
  { name: 'Relationships', href: '/relationships', icon: Waypoints, roles: EVERYONE },
  { name: 'Influence', href: '/influence-graph', icon: Network, roles: EVERYONE },
  { name: 'Monitoring', href: '/monitoring', icon: Radar, roles: EVERYONE },
  { name: 'Issues', href: '/issues', icon: Scale, roles: EVERYONE },
  { name: 'Search', href: '/search', icon: Search, roles: EVERYONE },
  { name: 'Notifications', href: '/notifications', icon: Bell, roles: EVERYONE },
];

export const ADMIN_NAV: NavItem[] = [
  { name: 'Data', href: '/admin', icon: Database, roles: ADMINS },
  { name: 'Duplicates', href: '/duplicates', icon: CopyCheck, roles: ADMINS },
  { name: 'AI', href: '/ai-settings', icon: Sparkles, roles: ADMINS },
  { name: 'Categories', href: '/taxonomies', icon: Tags, roles: ADMINS },
  { name: 'Audit', href: '/audit', icon: History, roles: ADMINS },
  { name: 'Access', href: '/access-control', icon: ShieldCheck, roles: ADMINS },
  { name: 'Users', href: '/users', icon: UserCog, roles: ADMINS },
  { name: 'Pending', href: '/pending', icon: Inbox, roles: ['super_admin', 'admin', 'top_owner'] },
];

export const visibleTo = (items: NavItem[], role?: Role): NavItem[] =>
  role ? items.filter((item) => item.roles.includes(role)) : [];

/** Whether a route belongs to a nav item (its page or a page below it) */
export const isActivePath = (pathname: string, href: string): boolean =>
  pathname === href || pathname.startsWith(`${href}/`);

export const displayName = (user: User | null | undefined): string => user?.firstName || user?.email || '';

export const roleLabel = (role: Role | undefined): string => {
  const text = (role ?? '').replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};
